import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { signInWithGoogle, auth } from '../services/firebase';
import { APP_NAME, APP_SUBHEADING } from '../constants';
import { Button } from '../components/Button';
import { Scan, Trophy, BrainCircuit, ShieldCheck, Zap, Activity } from 'lucide-react';

export const Login: React.FC = () => {
  const { user, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const currentDomain = window.location.hostname;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center z-50 relative">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-sm text-white font-bold font-mono uppercase tracking-[0.3em] animate-pulse">System Initializing</p>
        </div>
      </div>
    );
  }
  
  if (user) return <Navigate to="/" replace />;

  const handleLogin = async () => {
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err: any) {
      console.error("Login Error:", err);
      let msg = "Failed to sign in.";
      
      const code = err.code || (err.message && err.message.match(/\(([^)]+)\)/)?.[1]);
      
      if (code === 'auth/configuration-not-found') {
         msg = "SETUP REQUIRED: Google Sign-In is disabled. Go to Firebase Console -> Authentication -> Sign-in method -> Enable Google.";
      } else if (code === 'auth/unauthorized-domain' || (err.message && err.message.includes('unauthorized-domain'))) {
         msg = "DOMAIN_UNAUTHORIZED";
      } else if (code === 'auth/popup-closed-by-user') {
         msg = "Sign-in cancelled by user.";
      } else if (err.message) {
         msg = err.message;
      }
      
      setError(msg);
    }
  };

  const copyDomain = () => {
    navigator.clipboard.writeText(currentDomain);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const forceReload = () => {
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(function(registrations) {
            for(let registration of registrations) {
                registration.unregister();
            }
            window.location.reload();
        });
    } else {
        window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-black text-white selection:bg-white selection:text-black overflow-x-hidden">
       {/* Background Effects */}
       <div className="fixed inset-0 bg-[url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAADCAYAAABS3WWCAAAAE0lEQVQIW2NkQAKrVq36zwjjAwAoOQcLlp3fzgAAAABJRU5ErkJggg==')] opacity-10 pointer-events-none mix-blend-overlay z-0"></div>
       <div className="fixed inset-0 bg-gradient-to-b from-black via-[#09090b] to-black -z-10"></div>

       <div className="relative z-10 max-w-6xl mx-auto px-6 py-12 lg:py-24">
          
          {/* HERO SECTION: Split layout on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
            
            {/* Marketing Copy (Left) */}
            <div className="text-center lg:text-left order-2 lg:order-1">
               <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8 backdrop-blur-sm animate-in slide-in-from-bottom-4 duration-700 fade-in">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_#22c55e]"></span>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-300">System Online v1.0</span>
               </div>
               
               <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter mb-8 leading-[0.9] animate-in slide-in-from-bottom-8 duration-1000 fade-in fill-mode-backwards delay-100">
                 LEVEL UP <br/>
                 <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-500">BY VON</span>
               </h1>
               
               <p className="text-lg text-slate-400 mb-10 font-light leading-relaxed max-w-lg mx-auto lg:mx-0 animate-in slide-in-from-bottom-8 duration-1000 fade-in fill-mode-backwards delay-200">
                 The first AI-powered personal trainer that lives in your browser. 
                 Experience real-time form correction, global leaderboards, and personalized coaching—no downloads required.
               </p>

               <div className="flex flex-wrap justify-center lg:justify-start gap-6 text-xs font-mono uppercase tracking-widest text-slate-500 animate-in slide-in-from-bottom-8 duration-1000 fade-in fill-mode-backwards delay-300">
                  <div className="flex items-center gap-2 bg-surface px-3 py-2 rounded-sm border border-white/5">
                    <Zap className="w-4 h-4 text-white" /> No Downloads
                  </div>
                  <div className="flex items-center gap-2 bg-surface px-3 py-2 rounded-sm border border-white/5">
                    <ShieldCheck className="w-4 h-4 text-white" /> Privacy First
                  </div>
                  <div className="flex items-center gap-2 bg-surface px-3 py-2 rounded-sm border border-white/5">
                    <Activity className="w-4 h-4 text-white" /> Instant Feedback
                  </div>
               </div>
            </div>

            {/* Login Box (Right) */}
            <div className="order-1 lg:order-2 w-full max-w-md mx-auto animate-in fade-in zoom-in duration-1000 delay-150">
               <div className="bg-surface border border-white/10 p-8 rounded-sm relative overflow-hidden backdrop-blur-xl shadow-2xl">
                  {/* Accent Line */}
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-20"></div>
                  
                  <div className="text-center mb-8">
                    <div className="w-12 h-12 bg-white rounded-sm flex items-center justify-center text-black font-black text-2xl mx-auto mb-4">
                       L
                    </div>
                    <h2 className="text-xl font-bold text-white uppercase tracking-wide mb-1">Identity Verification</h2>
                    <p className="text-slate-500 text-xs font-mono">Access your secure training dashboard</p>
                  </div>

                  {error === "DOMAIN_UNAUTHORIZED" ? (
                      <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/50 rounded-sm text-left">
                          <div className="flex items-start gap-3 mb-3">
                              <div className="text-amber-500 text-xl">⚠️</div>
                              <div>
                                  <h3 className="text-amber-500 font-bold text-sm uppercase tracking-wide">Domain Not Authorized</h3>
                                  <p className="text-slate-300 text-xs font-mono mt-1">
                                    This domain needs to be added to Firebase.
                                  </p>
                              </div>
                          </div>
                          <div className="bg-black/50 p-3 border border-white/10 rounded-sm flex items-center justify-between mb-3 cursor-pointer" onClick={copyDomain}>
                              <code className="text-white font-mono text-xs truncate mr-2">{currentDomain}</code>
                              <span className="text-[10px] font-bold uppercase bg-white/10 px-2 py-1 rounded text-white">
                                  {copied ? 'Copied' : 'Copy'}
                              </span>
                          </div>
                      </div>
                  ) : error && (
                    <div className="mb-6 p-4 bg-red-900/30 border border-red-500/30 text-red-200 text-xs font-mono text-center">
                      <span className="font-bold block mb-1">ERROR</span>
                      {error}
                      <button onClick={forceReload} className="block w-full mt-3 px-3 py-1 bg-white text-black font-bold text-[10px] uppercase">
                          Retry
                      </button>
                    </div>
                  )}

                  <button
                    onClick={handleLogin}
                    className="w-full group relative flex items-center justify-center gap-3 bg-white hover:bg-gray-200 text-black font-bold py-4 px-6 transition-all duration-200 mb-6"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    <span className="uppercase tracking-wide">Sign in with Google</span>
                  </button>
                  
                  <div className="text-center">
                    <button 
                      onClick={() => setShowPrivacy(true)}
                      className="text-[10px] text-slate-400 hover:text-white border-b border-slate-600 hover:border-white transition-colors pb-0.5 font-mono uppercase"
                    >
                      Read Privacy & Data Pledge
                    </button>
                  </div>
               </div>
            </div>
          </div>

          {/* FEATURES GRID */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24 animate-in fade-in slide-in-from-bottom-10 duration-1000 delay-500 fill-mode-backwards">
              {/* Feature 1 */}
              <div className="p-8 bg-surface/50 border border-white/10 rounded-sm hover:border-white/30 hover:bg-surface transition-all duration-300 group">
                 <div className="w-12 h-12 bg-white/5 rounded-sm flex items-center justify-center mb-6 group-hover:bg-white group-hover:text-black transition-colors duration-300">
                    <Scan className="w-6 h-6" />
                 </div>
                 <h3 className="text-lg font-bold uppercase tracking-wide mb-3 group-hover:text-white transition-colors">Computer Vision</h3>
                 <p className="text-slate-400 text-sm leading-relaxed">
                   Advanced MediaPipe™ integration tracks 33 skeletal landmarks at 30fps. It's like having an elite coach watching every rep, ensuring perfect depth and lockout.
                 </p>
              </div>

              {/* Feature 2 */}
              <div className="p-8 bg-surface/50 border border-white/10 rounded-sm hover:border-white/30 hover:bg-surface transition-all duration-300 group">
                 <div className="w-12 h-12 bg-white/5 rounded-sm flex items-center justify-center mb-6 group-hover:bg-white group-hover:text-black transition-colors duration-300">
                    <Trophy className="w-6 h-6" />
                 </div>
                 <h3 className="text-lg font-bold uppercase tracking-wide mb-3 group-hover:text-white transition-colors">Time Attack</h3>
                 <p className="text-slate-400 text-sm leading-relaxed">
                   Gamify your pain. Compete in high-intensity, time-boxed challenges. Rank up on the global operator leaderboard based on speed and accuracy.
                 </p>
              </div>

              {/* Feature 3 */}
              <div className="p-8 bg-surface/50 border border-white/10 rounded-sm hover:border-white/30 hover:bg-surface transition-all duration-300 group">
                 <div className="w-12 h-12 bg-white/5 rounded-sm flex items-center justify-center mb-6 group-hover:bg-white group-hover:text-black transition-colors duration-300">
                    <BrainCircuit className="w-6 h-6" />
                 </div>
                 <h3 className="text-lg font-bold uppercase tracking-wide mb-3 group-hover:text-white transition-colors">AI Debrief</h3>
                 <p className="text-slate-400 text-sm leading-relaxed">
                   Powered by Gemini 2.5. Your workout logs are analyzed to provide actionable, military-grade feedback on your growth curve and form consistency.
                 </p>
              </div>
          </div>
          
          {/* FOOTER */}
          <div className="text-center border-t border-white/10 pt-12">
             <p className="text-slate-500 font-mono text-xs uppercase tracking-widest">
                {APP_NAME} {APP_SUBHEADING}
             </p>
          </div>
       </div>

       {/* PRIVACY MODAL */}
       {showPrivacy && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-surface border border-white/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 rounded-sm relative shadow-2xl">
              <div className="flex items-center gap-4 mb-6 border-b border-white/10 pb-6">
                 <div className="p-3 bg-green-500/10 rounded-full">
                   <ShieldCheck className="w-8 h-8 text-green-500" />
                 </div>
                 <div>
                   <h2 className="text-2xl font-black text-white uppercase tracking-tighter">Privacy Pledge</h2>
                   <p className="text-xs text-slate-400 font-mono uppercase tracking-widest">Transparency & Values</p>
                 </div>
              </div>

              <div className="space-y-6 text-sm text-slate-300 leading-relaxed font-light">
                <p className="text-lg text-white font-medium">
                  At {APP_NAME}, your data is yours. We process everything locally.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
                   <div className="bg-black/30 p-4 border border-white/5 rounded-sm">
                      <h3 className="text-white font-bold uppercase mb-2 text-xs tracking-wider">1. No Video Recording</h3>
                      <p className="text-slate-400 text-xs">
                        We strictly <span className="text-white font-bold">DO NOT</span> record or store any video. All camera processing happens locally on your device.
                      </p>
                   </div>
                   <div className="bg-black/30 p-4 border border-white/5 rounded-sm">
                      <h3 className="text-white font-bold uppercase mb-2 text-xs tracking-wider">2. Secure Cloud Sync</h3>
                      <p className="text-slate-400 text-xs">
                        We only store your numerical stats (reps, dates, scores) via Google Firebase so you can track progress.
                      </p>
                   </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white/10 flex justify-end">
                 <Button onClick={() => setShowPrivacy(false)}>I Understand</Button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};